const MAPS_SCRIPT_ID = "google-maps-js";

function getMapsBrowserKey(): string {
  return (process.env.MAPS_KEY || "").replace(/^&?key=/, "");
}

/**
 * Loads Google Maps JS once (browser key from env).
 * Needed because many screens use `google.maps.*` before `<Map />` mounts.
 * No bootstrap endpoint — key stays in env with HTTP referrer restrictions.
 */
export function loadGoogleMaps(): Promise<typeof google> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps requires a browser"));
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }

  const existing = document.getElementById(
    MAPS_SCRIPT_ID
  ) as HTMLScriptElement | null;

  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => {
        if (window.google?.maps) resolve(window.google);
        else reject(new Error("Google Maps failed to initialize"));
      });
      existing.addEventListener("error", () =>
        reject(new Error("Google Maps script failed to load"))
      );
    });
  }

  const key = getMapsBrowserKey();
  if (!key) {
    return Promise.reject(new Error("MAPS_KEY is not configured"));
  }

  return new Promise((resolve, reject) => {
    const language = navigator.language || "en";
    const script = document.createElement("script");
    script.id = MAPS_SCRIPT_ID;
    script.src = [
      "https://maps.googleapis.com/maps/api/js",
      `?key=${encodeURIComponent(key)}`,
      `&language=${encodeURIComponent(language)}`,
      "&libraries=geometry",
    ].join("");
    script.async = true;
    script.onload = () => {
      if (window.google?.maps) resolve(window.google);
      else reject(new Error("Google Maps failed to initialize"));
    };
    script.onerror = () =>
      reject(new Error("Google Maps script failed to load"));
    document.head.appendChild(script);
  });
}
